import { useState, useEffect, useRef } from "react";
import { GlassCard, N8N_BASE, FF, SPill, fmt, TabBar, SkeletonRows, StatCard } from "../shared";

const FINANCIAL_URL = `${N8N_BASE}/qc-financial-query`;
const INVOICES_URL  = `${N8N_BASE}/qc-invoices-get`;

// ── Financial query ────────────────────────────────────────────────────────────
async function financialQuery(query_type, filters) {
  const body = filters ? { query_type, filters } : { query_type };
  const r = await fetch(FINANCIAL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await r.text();
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${text.slice(0, 200)}`);
  try { return { data: JSON.parse(text), rawText: text }; }
  catch { throw new Error(`Non-JSON response: ${text.slice(0, 200)}`); }
}

// ── Response normaliser ────────────────────────────────────────────────────────
function parseResponse(raw) {
  const unwrapped = Array.isArray(raw) ? (raw.length === 1 ? raw[0] : raw) : raw;
  const root = unwrapped?.data || unwrapped?.result || unwrapped;
  const monthsRaw =
    root?.monthly || root?.months || root?.monthly_breakdown ||
    root?.cashflow?.months || null;
  const pipelineRaw =
    root?.pipeline || root?.draft_invoices || root?.cashflow?.pipeline || null;
  const pipeline = Array.isArray(pipelineRaw)
    ? pipelineRaw
    : pipelineRaw?.invoices || [];
  const summary = root?.summary || root?.totals || root?.cashflow?.summary || null;
  let months = Array.isArray(monthsRaw) ? monthsRaw : [];
  if (!months.length && Array.isArray(unwrapped) && unwrapped.length > 1) {
    const first = unwrapped[0];
    if (first?.month || first?.month_label || first?.label || first?.planned !== undefined)
      months = unwrapped;
  }
  return { months, pipeline, summary };
}

// ── Date helpers ───────────────────────────────────────────────────────────────
function parseDate(s) {
  if (!s) return null;
  // DD/MM/YYYY legacy format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split("/");
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  // YYYY-MM-DD (and YYYY-MM-DDTHH:mm:ssZ)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(s);
}

function fmtMonthKey(d) {
  if (!d || isNaN(d.getTime())) return "unknown";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtMonthLabel(key) {
  if (key === "unknown") return "No Date";
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString("en-CA", { month: "long", year: "numeric" });
}

function isOverdue(inv) {
  if (inv.status === "paid") return false;
  const d = parseDate(inv.due_date || inv.due);
  return d ? d < new Date() : false;
}

// ── Group invoices by month (by due_date, fallback billed_date) ────────────────
function groupByMonth(invoices) {
  const map = {};
  for (const inv of invoices) {
    const d = parseDate(inv.due_date) || parseDate(inv.billed_date);
    const key = fmtMonthKey(d);
    if (!map[key]) map[key] = [];
    map[key].push(inv);
  }
  const keys = Object.keys(map).sort((a, b) => {
    if (a === "unknown") return 1;
    if (b === "unknown") return -1;
    return b.localeCompare(a); // descending: newest month first
  });
  return keys.map(key => ({
    key,
    label: fmtMonthLabel(key),
    invoices: [...map[key]].sort((a, b) => {
      const da = parseDate(a.due_date) || 0;
      const db = parseDate(b.due_date) || 0;
      return da - db;
    }),
  }));
}

// ── Inline glass style ─────────────────────────────────────────────────────────
const G = {
  background: "rgba(255,255,255,0.52)",
  backdropFilter: "blur(40px) saturate(180%)",
  WebkitBackdropFilter: "blur(40px) saturate(180%)",
  border: "0.5px solid rgba(255,255,255,0.85)",
  borderRadius: 16,
  overflow: "hidden",
};


function ProgressBar({ paid, total }) {
  const pct = total > 0 ? Math.min(100, (Number(paid) / Number(total)) * 100) : 0;
  return (
    <div style={{ height: 4, borderRadius: 3, background: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.75)", overflow: "hidden", margin: "8px 0 6px" }}>
      {pct > 0 && (
        <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,rgba(52,199,89,0.55),rgba(52,199,89,0.8))", borderRadius: 3, transition: "width 0.6s ease" }} />
      )}
    </div>
  );
}

function InvoiceRow({ inv, last }) {
  const total   = Number(inv.invoice_total || inv.total_amount || inv.amount || 0);
  const due     = parseDate(inv.due_date || inv.due);
  const overdue = isOverdue(inv);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "11px 20px",
      borderBottom: last ? "none" : "0.5px solid rgba(0,0,0,0.04)",
      background: inv.status === "paid"
        ? "rgba(52,199,89,0.04)"
        : overdue ? "rgba(255,149,0,0.04)" : "transparent",
      flexWrap: "wrap",
    }}>
      <div style={{
        width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
        background: inv.status === "paid"
          ? "rgba(52,199,89,0.7)"
          : overdue ? "rgba(255,149,0,0.6)" : "rgba(60,60,67,0.18)",
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#1c1c1e", fontFamily: FF }}>
            {inv.campaign_name || inv.campaign || "—"}
          </span>
          {inv.brand && (
            <span style={{ fontSize: 11, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>{inv.brand}</span>
          )}
          <SPill status={inv.status || "draft"} />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {inv.invoice_number && (
            <span style={{ fontSize: 11, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>#{inv.invoice_number}</span>
          )}
          {due && (
            <span style={{ fontSize: 11, fontFamily: FF, fontWeight: overdue ? 600 : 400, color: overdue ? "rgba(170,90,0,0.85)" : "rgba(60,60,67,0.4)" }}>
              Due {due.toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
              {overdue ? " · overdue" : ""}
            </span>
          )}
        </div>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.3px", fontFamily: FF, color: inv.status === "paid" ? "rgba(30,122,60,0.85)" : "#1c1c1e" }}>
          ${fmt(total)}
        </div>
        <div style={{ fontSize: 10, color: "rgba(60,60,67,0.35)", fontFamily: FF }}>incl. HST</div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function CashflowPage() {
  const [tab, setTab] = useState("cashflow");

  // Cash Flow tab
  const [months,    setMonths]    = useState([]);
  const [pipeline,  setPipeline]  = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [cfLoading, setCfLoading] = useState(false);
  const [cfError,   setCfError]   = useState(null);
  const [rawDebug,  setRawDebug]  = useState(null);
  const [expanded,  setExpanded]  = useState({});

  // Monthly tab
  const [invoices,  setInvoices]  = useState([]);
  const [mLoading,  setMLoading]  = useState(false);
  const [mError,    setMError]    = useState(null);
  const [mExpanded, setMExpanded] = useState({});
  const mFetched = useRef(false);

  // ── Fetch cash flow ──────────────────────────────────────────────────────────
  const fetchCashflow = async () => {
    setCfLoading(true); setCfError(null); setRawDebug(null);
    try {
      const { data, rawText } = await financialQuery("cashflow");
      const parsed = parseResponse(data);
      setMonths(parsed.months);
      setPipeline(parsed.pipeline);
      setSummary(parsed.summary);
      if (!parsed.months.length && !parsed.pipeline.length)
        setRawDebug(rawText.slice(0, 800));
    } catch (e) {
      setCfError(e.message || "Could not load cashflow data.");
    } finally {
      setCfLoading(false);
    }
  };

  // ── Fetch invoices ───────────────────────────────────────────────────────────
  const fetchInvoices = async () => {
    setMLoading(true); setMError(null);
    try {
      const r = await fetch(INVOICES_URL);
      const text = await r.text();
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = JSON.parse(text);
      const list = Array.isArray(d) ? (d[0]?.invoices || d) : (d.invoices || []);
      setInvoices(list);
      mFetched.current = true;
    } catch (e) {
      setMError(e.message || "Could not load invoices.");
    } finally {
      setMLoading(false);
    }
  };

  // Fire both fetches in parallel on mount — monthly tab is instant when you switch
  useEffect(() => {
    fetchCashflow();
    if (!mFetched.current) { mFetched.current = true; fetchInvoices(); }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (tab === "monthly" && !mFetched.current && !mLoading) {
      fetchInvoices();
    }
  }, [tab]); // eslint-disable-line

  const toggleCf = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));
  const toggleM  = (key) => setMExpanded(e => ({ ...e, [key]: !e[key] }));

  // ── Cash flow computed ───────────────────────────────────────────────────────
  const grandPlanned = summary?.total_planned
    ?? ((summary?.total_paid ?? 0) + (summary?.total_unpaid ?? 0))
    ?? months.reduce((s, m) => s + Number(m.planned || m.total_planned || 0), 0);
  const grandPaid   = summary?.total_paid
    ?? months.reduce((s, m) => s + Number(m.paid || m.total_paid || 0), 0);
  const grandRemain = summary?.total_unpaid
    ?? summary?.total_remaining
    ?? (grandPlanned - grandPaid);
  const pipelineTotal = summary?.total_pipeline
    ?? pipeline.reduce((s, i) => s + Number(i.total_amount || i.invoice_total || 0), 0);
  const cfInvCount = months.reduce((s, m) => s + (m.invoices?.length || m.invoice_count || 0), 0)
    + pipeline.length;

  // ── Monthly computed ─────────────────────────────────────────────────────────
  const mTotal       = invoices.reduce((s, i) => s + Number(i.invoice_total || 0), 0);
  const mPaid        = invoices.filter(i => i.status === "paid").reduce((s, i) => s + Number(i.invoice_total || 0), 0);
  const mOutstanding = mTotal - mPaid;
  const monthGroups  = groupByMonth(invoices);

  // ── Sort Cash Flow months descending (newest first) ──────────────────────────
  function cfMonthSortKey(m) {
    // Prefer explicit key fields in YYYY-MM format
    const raw = m.month_key || m.key || m.month || m.label || m.month_label || "";
    // If it already looks like YYYY-MM, use directly
    if (/^\d{4}-\d{2}/.test(raw)) return raw;
    // Try to parse a date label like "July 2026" or "Jul 2026"
    const d = new Date(raw);
    if (!isNaN(d)) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return raw;
  }
  const sortedMonths = [...months].sort((a, b) =>
    cfMonthSortKey(b).localeCompare(cfMonthSortKey(a))
  );

  // ── UI helpers ───────────────────────────────────────────────────────────────
  const loading  = tab === "cashflow" ? cfLoading : mLoading;
  const subtitle = tab === "cashflow"
    ? (cfLoading ? "Loading…" : `${cfInvCount} invoices · FY2026`)
    : (mLoading ? "Loading…" : invoices.length ? `${invoices.length} invoices` : "");

  const TABS = [
    { id: "cashflow", label: "Cash Flow" },
    { id: "monthly",  label: "Monthly" },
  ];

  return (
    <div style={{ height: "calc(100vh - 24px)", display: "flex", flexDirection: "column", fontFamily: FF }}>
      <GlassCard style={{ maxWidth: "none", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ── Fixed header ───────────────────────────────────────────────────── */}
        <div style={{ flexShrink: 0, padding: "20px 24px 0" }}>

          {/* Title + refresh */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: "rgba(255,255,255,0.55)",
                border: "0.5px solid rgba(255,255,255,0.88)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="rgba(50,60,90,0.6)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6"  y1="20" x2="6"  y2="14"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.5px" }}>Cash Flow</div>
                <div style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", marginTop: 1 }}>{subtitle}</div>
              </div>
            </div>

            <button
              onClick={tab === "cashflow" ? fetchCashflow : fetchInvoices}
              disabled={loading}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 10,
                background: "rgba(255,255,255,0.52)",
                border: "0.5px solid rgba(255,255,255,0.82)",
                color: "rgba(60,60,67,0.6)", fontSize: 12, fontWeight: 500,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1, fontFamily: FF,
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: loading ? "spin 1s linear infinite" : "none" }}>
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
              </svg>
              Refresh
            </button>
          </div>

          {/* Tab bar */}
          <TabBar tabs={TABS} active={tab} onChange={setTab} />
        </div>

        {/* ── Scrollable content ──────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "16px 24px 24px" }}>

          {/* Stats strip — always reserve space to avoid layout shift */}
          {tab === "cashflow" && (
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              {cfLoading ? (
                <>
                  {[0,1,2].map(i => <div key={i} style={{ flex: 1, height: 76, borderRadius: 14, background: "rgba(255,255,255,0.45)", border: "0.5px solid rgba(255,255,255,0.78)", animation: "skpulse 1.4s ease-in-out infinite", animationDelay: `${i*0.08}s` }} />)}
                </>
              ) : !cfError ? (
                <>
                  <StatCard label="Total Planned" value={`$${fmt(grandPlanned)}`} sub={`${cfInvCount} invoices`} />
                  <StatCard label="Total Paid" value={`$${fmt(grandPaid)}`} sub={`${months.reduce((s, m) => s + (m.invoices?.filter(i => i.status === "paid").length || m.paid_count || 0), 0)} paid`} accent="rgba(235,252,240,0.75)" />
                  <StatCard label="Remaining" value={`$${fmt(grandRemain)}`} sub={grandRemain <= 0 ? "All settled" : undefined} />
                </>
              ) : null}
            </div>
          )}
          {tab === "monthly" && (
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              {mLoading ? (
                <>
                  {[0,1,2].map(i => <div key={i} style={{ flex: 1, height: 76, borderRadius: 14, background: "rgba(255,255,255,0.45)", border: "0.5px solid rgba(255,255,255,0.78)", animation: "skpulse 1.4s ease-in-out infinite", animationDelay: `${i*0.08}s` }} />)}
                </>
              ) : !mError && invoices.length > 0 ? (
                <>
                  <StatCard label="Total Invoiced" value={`$${fmt(mTotal)}`} sub={`${invoices.length} invoices`} />
                  <StatCard label="Paid" value={`$${fmt(mPaid)}`} sub={`${invoices.filter(i => i.status === "paid").length} paid`} accent="rgba(235,252,240,0.75)" />
                  <StatCard label="Outstanding" value={`$${fmt(mOutstanding)}`} />
                </>
              ) : null}
            </div>
          )}

          {loading && <SkeletonRows count={5} height={64} />}

          {/* ── CASH FLOW TAB ────────────────────────────────────────────────── */}
          {tab === "cashflow" && !cfLoading && (
            <>
              {cfError && (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 14, color: "rgba(60,60,67,0.5)", marginBottom: 12, fontFamily: FF }}>{cfError}</div>
                  <button onClick={fetchCashflow} style={{ padding: "7px 18px", background: "rgba(20,22,30,0.85)", border: "none", borderRadius: 9, color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FF }}>
                    Retry
                  </button>
                </div>
              )}

              {!cfError && months.length === 0 && pipeline.length === 0 && (
                <div style={{ padding: "24px", ...G, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(60,60,67,0.6)", fontFamily: FF, marginBottom: 8 }}>
                    No data parsed — raw response:
                  </div>
                  <pre style={{ fontSize: 11, color: "rgba(60,60,67,0.7)", fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all", margin: 0, background: "rgba(0,0,0,0.03)", padding: "10px 12px", borderRadius: 8, maxHeight: 220, overflowY: "auto" }}>
                    {rawDebug || "(empty response)"}
                  </pre>
                </div>
              )}

              {!cfError && sortedMonths.map((month, mIdx) => {
                const key       = month.month_key || month.key || `month-${mIdx}`;
                const label     = month.label || month.month || month.month_label || key;
                const planned   = Number(month.planned || month.total_planned || 0);
                const remaining = Number(month.remaining || month.total_remaining || 0);
                const paid      = Number(month.paid || month.total_paid || (remaining >= 0 ? planned - remaining : 0));
                const invs      = month.invoices || [];
                const invCount  = invs.length || month.invoice_count || 0;
                const paidCount = invs.filter(i => i.status === "paid").length || month.paid_count || 0;
                const isOpen    = expanded[key] !== false;
                const hasOverdue = invs.some(isOverdue);

                return (
                  <div key={key} style={{ marginBottom: 10 }}>
                    <div style={G}>
                      <button
                        onClick={() => toggleCf(key)}
                        style={{
                          width: "100%", display: "flex", alignItems: "flex-start",
                          justifyContent: "space-between", gap: 12,
                          padding: "16px 20px 14px",
                          background: "none", border: "none", cursor: "pointer",
                          borderBottom: isOpen && invs.length ? "0.5px solid rgba(0,0,0,0.05)" : "none",
                          fontFamily: FF, textAlign: "left",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.3px" }}>{label}</span>
                            {hasOverdue && (
                              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: "rgba(255,149,0,0.11)", color: "#8c5000", border: "1px solid rgba(255,149,0,0.2)" }}>
                                overdue
                              </span>
                            )}
                            <span style={{ fontSize: 10, color: "rgba(60,60,67,0.38)" }}>
                              {invCount} invoice{invCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <ProgressBar paid={paid} total={planned} />
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, color: "rgba(30,122,60,0.85)", fontWeight: 600 }}>${fmt(paid)} paid</span>
                            <span style={{ fontSize: 11, color: "rgba(60,60,67,0.45)" }}>${fmt(remaining)} remaining</span>
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 18, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.5px" }}>${fmt(planned)}</div>
                          <div style={{ fontSize: 10, color: "rgba(60,60,67,0.38)", marginTop: 2 }}>{paidCount}/{invCount} paid</div>
                        </div>
                        {invs.length > 0 && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(60,60,67,0.35)"
                            strokeWidth="2.2" strokeLinecap="round"
                            style={{ marginTop: 5, flexShrink: 0, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "none" }}>
                            <polyline points="6,9 12,15 18,9"/>
                          </svg>
                        )}
                      </button>

                      {isOpen && invs.length > 0 && (
                        <div>
                          {[...invs]
                            .sort((a, b) => (parseDate(a.due_date || a.due) || 0) - (parseDate(b.due_date || b.due) || 0))
                            .map((inv, idx) => (
                              <InvoiceRow key={inv.invoice_id || idx} inv={inv} last={idx === invs.length - 1} />
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {!cfError && pipeline.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={G}>
                    <div style={{ padding: "14px 20px", borderBottom: "0.5px solid rgba(0,0,0,0.05)", background: "rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(60,60,67,0.45)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
                        </svg>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1c1c1e", fontFamily: FF }}>Pipeline</span>
                        <span style={{ fontSize: 11, color: "rgba(60,60,67,0.4)", fontFamily: FF }}>
                          — {pipeline.length} not yet scheduled
                        </span>
                      </div>
                      {pipelineTotal > 0 && (
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.4px", fontFamily: FF }}>
                          ${fmt(pipelineTotal)}
                        </div>
                      )}
                    </div>
                    {pipeline.map((inv, idx) => {
                      const total = Number(inv.total_amount || inv.invoice_total || inv.amount || 0);
                      return (
                        <div
                          key={inv.invoice_id || idx}
                          style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "10px 20px",
                            borderBottom: idx < pipeline.length - 1 ? "0.5px solid rgba(0,0,0,0.04)" : "none",
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "rgba(60,60,67,0.15)", flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 13, fontWeight: 500, color: "#1c1c1e", fontFamily: FF }}>
                                {inv.campaign_name || inv.campaign || "—"}
                              </span>
                              {inv.brand && <span style={{ fontSize: 11, color: "rgba(60,60,67,0.42)", fontFamily: FF }}>{inv.brand}</span>}
                              <SPill status={inv.status || "draft"} />
                            </div>
                          </div>
                          {total > 0 && (
                            <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(60,60,67,0.65)", fontFamily: FF, flexShrink: 0 }}>
                              ${fmt(total)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── MONTHLY TAB ──────────────────────────────────────────────────── */}
          {tab === "monthly" && !mLoading && (
            <>
              {mError && (
                <div style={{ padding: "40px 0", textAlign: "center" }}>
                  <div style={{ fontSize: 14, color: "rgba(60,60,67,0.5)", marginBottom: 12, fontFamily: FF }}>{mError}</div>
                  <button onClick={fetchInvoices} style={{ padding: "7px 18px", background: "rgba(20,22,30,0.85)", border: "none", borderRadius: 9, color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FF }}>
                    Retry
                  </button>
                </div>
              )}

              {!mError && invoices.length === 0 && (
                <div style={{ padding: "56px 0", textAlign: "center", color: "rgba(60,60,67,0.35)", fontSize: 14, fontFamily: FF }}>
                  No invoices found.
                </div>
              )}

              {!mError && monthGroups.map(({ key, label, invoices: invs }) => {
                const isOpen     = mExpanded[key] !== false;
                const total      = invs.reduce((s, i) => s + Number(i.invoice_total || 0), 0);
                const paid       = invs.filter(i => i.status === "paid").reduce((s, i) => s + Number(i.invoice_total || 0), 0);
                const paidCount  = invs.filter(i => i.status === "paid").length;
                const hasOverdue = invs.some(isOverdue);

                return (
                  <div key={key} style={{ marginBottom: 10 }}>
                    <div style={G}>
                      <button
                        onClick={() => toggleM(key)}
                        style={{
                          width: "100%", display: "flex", alignItems: "flex-start",
                          justifyContent: "space-between", gap: 12,
                          padding: "16px 20px 14px",
                          background: "none", border: "none", cursor: "pointer",
                          borderBottom: isOpen ? "0.5px solid rgba(0,0,0,0.05)" : "none",
                          fontFamily: FF, textAlign: "left",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 15, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.3px" }}>{label}</span>
                            {hasOverdue && (
                              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20, background: "rgba(255,149,0,0.11)", color: "#8c5000", border: "1px solid rgba(255,149,0,0.2)" }}>
                                overdue
                              </span>
                            )}
                            <span style={{ fontSize: 10, color: "rgba(60,60,67,0.38)" }}>
                              {invs.length} invoice{invs.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <ProgressBar paid={paid} total={total} />
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 11, color: "rgba(30,122,60,0.85)", fontWeight: 600 }}>${fmt(paid)} paid</span>
                            <span style={{ fontSize: 11, color: "rgba(60,60,67,0.45)" }}>${fmt(total - paid)} remaining</span>
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 18, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.5px" }}>${fmt(total)}</div>
                          <div style={{ fontSize: 10, color: "rgba(60,60,67,0.38)", marginTop: 2 }}>{paidCount}/{invs.length} paid</div>
                        </div>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(60,60,67,0.35)"
                          strokeWidth="2.2" strokeLinecap="round"
                          style={{ marginTop: 5, flexShrink: 0, transition: "transform 0.2s", transform: isOpen ? "rotate(180deg)" : "none" }}>
                          <polyline points="6,9 12,15 18,9"/>
                        </svg>
                      </button>

                      {isOpen && (
                        <div>
                          {invs.map((inv, idx) => (
                            <InvoiceRow
                              key={inv.row_number || inv.invoice_id || idx}
                              inv={inv}
                              last={idx === invs.length - 1}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </GlassCard>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
